import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import axios from "axios"; import { handleAuthError } from "../../utils";
import withAuth from "../withAuth";
import { 
  Col, 
  Container, 
  Row, 
  Modal, 
  ModalHeader, 
  ModalBody, 
  Card, 
  CardBody, 
  Badge, 
  Input,
  Spinner
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseUrl } from '../../Network';

const ManageFaculty = () => {
  document.title = "Manage Faculty";

  const [loading, setLoading] = useState(true);
  const [teachers, setTeachers] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [teacherPrograms, setTeacherPrograms] = useState([]);
  const [viewModal, setViewModal] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCohort, setSelectedCohort] = useState("none");
  const [isLoadingPrograms, setIsLoadingPrograms] = useState(false);

  useEffect(() => {
    fetchCohorts();
    if (selectedCohort !== "none") {
      fetchTeachers();
    }
  }, [selectedCohort]);

  const fetchCohorts = async () => {
    try {
      const response = await axios.get(`${baseUrl}/faculty/cohort/all`);
      setCohorts(response.data.data);
    } catch (error) { handleAuthError(error)
      console.error("Error fetching cohorts:", error);
    }
  };

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const endpoint = `${baseUrl}/faculty/teacher/all`;
      
      const response = await axios.get(endpoint);
      setTeachers(response.data.data);
      setLoading(false);
    } catch (error) { handleAuthError(error)
      console.error("Error:", error);
      setLoading(false);
    }
  };

  const fetchTeacherPrograms = async (teacherId) => {
    try {
      setIsLoadingPrograms(true);
      const response = await axios.get(`${baseUrl}/faculty/teacher/programs/${Number(selectedCohort)}/${teacherId}`);
      setTeacherPrograms(response.data.data);
      setIsLoadingPrograms(false);
    } catch (error) { handleAuthError(error)
      console.error("Error fetching teacher programs:", error);
      setTeacherPrograms([]);
      setIsLoadingPrograms(false);
    }
  };

  const handleViewTeacher = async (teacher) => {
    setSelectedTeacher(teacher);
    await fetchTeacherPrograms(teacher.id);
    setViewModal(true);
  };

  const toggleViewModal = () => {
    setViewModal(!viewModal);
  };

  const toggleInviteModal = () => {
    setInviteModalOpen(!inviteModalOpen);
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredTeachers = teachers.filter(teacher => {
    const searchString = `${teacher.firstName} ${teacher.lastName} ${teacher.email}`.toLowerCase();
    return searchString.includes(searchQuery.toLowerCase());
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleCohortChange = (e) => {
    setSelectedCohort(e.target.value);
  };

  return (
    <React.Fragment>
      <div className="page-content pt-5">
        <Container>
          <Breadcrumbs title=" " breadcrumbItem="Manage Faculty" />
          <Row>
            <Col lg="12">
              <Row className="align-items-center mb-3">
                <Col md={6}>
                  <div className="mb-3">
                    <h5 className="card-title">
                      Teachers List{" "}
                      <span className="text-muted fw-normal ms-2">
                        ({teachers.length})
                      </span>
                    </h5>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex flex-wrap align-items-center justify-content-end gap-2 mb-3">
                    <div className="me-2">
                      <select 
                        className="form-select"
                        value={selectedCohort}
                        onChange={handleCohortChange}
                      >
                        <option value="none">All Cohorts</option>
                        {cohorts.map((cohort) => (
                          <option key={cohort.id} value={cohort.id}>
                            {cohort.title}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="search-box">
                      <Input
                        type="text"
                        className="form-control"
                        placeholder="Search teachers..."
                        value={searchQuery}
                        onChange={handleSearch}
                        disabled={selectedCohort === "none"}
                      />
                      <i className="ri-search-line search-icon"></i>
                    </div>
                  </div>
                </Col>
              </Row>

              <Card>
                <CardBody>
                  {selectedCohort === "none" ? (
                    <div className="text-center mt-5">
                      <h3>Please select a cohort to view teachers</h3>
                    </div>
                  ) : loading ? (
                    <div className="text-center mt-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : filteredTeachers.length === 0 ? (
                    <div className="text-center mt-5">
                      <h3>No teachers found for this cohort</h3>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-centered table-hover align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>#</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Country</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredTeachers.map((teacher, index) => (
                            <tr key={teacher.id}>
                              <td>{index + 1}</td>
                              <td>
                                <h5 className="font-size-14 mb-1">
                                  {teacher.firstName} {teacher.lastName}
                                </h5>
                                <p className="text-muted mb-0">{teacher.gender}</p>
                              </td>
                              <td>{teacher.email}</td>
                              <td>{teacher.phone || 'N/A'}</td>
                              <td>{teacher.country || 'N/A'}</td>
                              <td>
                                <Badge color={teacher.status ? "success" : "danger"}>
                                  {teacher.status ? "Active" : "Inactive"}
                                </Badge>
                              </td>
                              <td>
                                <button
                                  className="btn btn-light btn-sm"
                                  onClick={() => handleViewTeacher(teacher)}
                                >
                                  <i className="mdi mdi-eye-outline me-1"></i> View
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Teacher Details Modal */}
      <Modal isOpen={viewModal} toggle={toggleViewModal} size="lg">
        <ModalHeader toggle={toggleViewModal}>
          Teacher Details: {selectedTeacher?.firstName} {selectedTeacher?.lastName}
        </ModalHeader>
        <ModalBody>
          {selectedTeacher && (
            <div className="teacher-details">
              <Row>
                <Col md={6}>
                  <div className="mb-3">
                    <h6>Personal Information</h6>
                    <hr />
                    <p><strong>Name:</strong> {selectedTeacher.firstName} {selectedTeacher.lastName}</p>
                    <p><strong>Email:</strong> {selectedTeacher.email}</p>
                    <p><strong>Phone:</strong> {selectedTeacher.phone || 'N/A'}</p>
                    <p><strong>Gender:</strong> {selectedTeacher.gender}</p>
                    <p><strong>Country:</strong> {selectedTeacher.country || 'N/A'}</p>
                    <p><strong>Timezone:</strong> {selectedTeacher.timezone || 'N/A'}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <h6>Professional Information</h6>
                    <hr />
                    <p><strong>Qualification:</strong> {selectedTeacher.qualification || 'N/A'}</p>
                    <p><strong>Experience:</strong> {selectedTeacher.experience || 'N/A'}</p>
                    <p><strong>Class Link:</strong> 
                      {selectedTeacher.classLink ? (
                        <a href={selectedTeacher.classLink} target="_blank" rel="noopener noreferrer">
                          Join Class
                        </a>
                      ) : 'N/A'}
                    </p>
                    <p><strong>Document:</strong> 
                      {selectedTeacher.docUrl ? (
                        <a href={selectedTeacher.docUrl} target="_blank" rel="noopener noreferrer">
                          View Document
                        </a>
                      ) : 'N/A'}
                    </p>
                    <p><strong>Status:</strong> 
                      <Badge color={selectedTeacher.status ? "success" : "danger"} className="ms-2">
                        {selectedTeacher.status ? "Active" : "Inactive"}
                      </Badge>
                    </p>
                  </div>
                </Col>
              </Row>

              <div className="mt-4">
                <h6>Assigned Programs ({teacherPrograms.length})</h6>
                <hr />
                {isLoadingPrograms ? (
                  <div className="text-center">
                    <Spinner size="sm" /> Loading programs...
                  </div>
                ) : teacherPrograms.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover">
                      <thead>
                        <tr>
                          <th>Student</th>
                          <th>Program</th>
                          <th>Level</th>
                          <th>Next Class</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teacherPrograms.map(program => (
                          <tr key={program.id}>
                            <td>
                              {program.child?.firstName} {program.child?.lastName}
                              <br />
                              <small className="text-muted">
                                Parent: {program.child?.parent?.firstName} {program.child?.parent?.lastName}
                              </small>
                            </td>
                            <td>{program.package?.name}</td>
                            <td>Level {program.level}</td>
                            <td>{formatDate(program.nextClassDate)}</td>
                            <td>
                              <Badge color={program.status ? "success" : "danger"}>
                                {program.status ? "Active" : "Inactive"}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="alert alert-info">
                    This teacher is not currently assigned to any programs.
                  </div>
                )}
              </div>
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* Invite Teacher Modal */}
      <Modal isOpen={inviteModalOpen} toggle={toggleInviteModal}>
        <ModalHeader toggle={toggleInviteModal}>Invite Teacher</ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <label className="form-label">Email Address</label>
            <input
              type="email"
              className="form-control"
              placeholder="Enter teacher's email"
            />
          </div>
          <div className="d-flex justify-content-end gap-2">
            <button className="btn btn-light" onClick={toggleInviteModal}>
              Cancel
            </button>
            <button className="btn btn-primary">
              Send Invitation
            </button>
          </div>
        </ModalBody>
      </Modal>

      <ToastContainer />
    </React.Fragment>
  );
};

export default withAuth(ManageFaculty);